import { useEffect, useId, useRef, useState } from "react";
import { replyAllLabel, replyToneChoices, type Tone } from "../email";
import type { OptionView, PersonCard } from "../types";
import { Parts } from "./PersonRef";

/**
 * Step two of a written reply (21 §4b): the player has chosen what to say and now chooses how.
 * Only the tones the option offers are shown. Each says how it may come across; none is marked
 * as right. Nothing afterwards says whether the reply was misread.
 */
export function TonePicker({ option, busy, onSend, onBack, sendLabel = "Send", shortcuts = false, cc }: {
  option: OptionView; busy: boolean; onSend: (tone: Tone) => void; onBack: () => void; sendLabel?: string; shortcuts?: boolean;
  /** People copied in on the email: a reply-all goes to them too (21 §4d). */
  cc?: PersonCard[] | undefined;
}) {
  const [tone, setTone] = useState<Tone | null>(null);
  const ids = useId();
  const first = useRef<HTMLInputElement>(null);
  const choices = replyToneChoices(option.tones, cc, option.replyAll);
  const toAll = replyAllLabel(option, cc);
  useEffect(() => { first.current?.focus({ preventScroll: true }); }, [option.id]);

  // Number keys pick a tone while the picker is open (only when single-key shortcuts are on).
  useEffect(() => {
    if (!shortcuts) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey || document.querySelector("dialog[open]")) return;
      const el = document.activeElement;
      if (el && ((el.tagName === "INPUT" && (el as HTMLInputElement).type !== "radio") || el.tagName === "TEXTAREA" || el.tagName === "SELECT")) return;
      const n = /^[1-9]$/.test(e.key) ? Number(e.key) - 1 : -1;
      const c = choices[n];
      if (c) { e.preventDefault(); e.stopPropagation(); setTone(c.id); }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [shortcuts, choices]);

  return (
    <div className="tone-picker">
      {toAll && <p className="reply-all-note"><span aria-hidden="true">⇉ </span>{toAll}</p>}
      <p className="tone-picker__reply"><span className="muted">{toAll ? "Your reply to all: " : "Your reply: "}</span>“<Parts parts={option.textParts} text={option.text} />”</p>
      <fieldset className="segmented">
        <legend id={`${ids}-q`}>How do you put it?</legend>
        <div className="segmented__row">
          {choices.map((c, i) => (
            <label key={c.id} className="segmented__opt">
              <input ref={i === 0 ? first : undefined} type="radio" name={`${ids}-tone`} value={c.id} checked={tone === c.id}
                onChange={() => setTone(c.id)} aria-describedby={`${ids}-${c.id}`} />
              <span>{c.label}</span>
            </label>
          ))}
        </div>
      </fieldset>
      <ul className="tone-picker__notes">
        {choices.map((c) => (
          <li key={c.id} id={`${ids}-${c.id}`} className={tone === c.id ? "is-selected" : undefined}>
            <strong>{c.label}.</strong> {c.description}
          </li>
        ))}
      </ul>
      <div className="row-actions">
        <button type="button" className="btn btn--primary" disabled={busy || tone === null} onClick={() => tone && onSend(tone)}>
          {sendLabel}
        </button>
        <button type="button" className="btn btn--quiet" onClick={onBack}>Choose a different reply</button>
      </div>
      {tone === null && <p className="muted small">Choose a tone to send it.</p>}
    </div>
  );
}
